import React, { ReactElement, useState } from 'react';
import {
  UserSubscriptionStripeConnectStatus,
  RevenueShare,
  TAX_LABEL,
} from '../../../../ReduxStorage/UserSubscriptionPlans/constants';
import { AlertMessage } from '../../../components/AlertMessage/component';
import { Chip } from '../../../components/Chip/component';
import { Select } from '../../../components/Select/component';
import {
  getChipVariantFromStripeConnectStatus,
  makeCountryOptions,
} from './helpers';
import { StripeConnectCountries } from './constants';
import { capitalizeFirstLetter } from '../../../utilities/utilities';
import { Table } from '../ManageUsers/styles';
import {
  ActionsContainer,
  BannerContainer,
  BannerLinkStyle,
  Header,
  Heading,
  InputContainer,
  NeedHelpLinkStyle,
  SelectErrorContainer,
  StyledButton,
} from './styles';
import { ActionButton } from '../../../components/Buttons/ActionButton/ActionButton';
import { ROUTE_CONTACT_PAGE } from '../../../../ReduxStorage/routeActions';
import { RouteLink } from '../../../components/RouteLink/component';
import { SubscriptionPlanType } from '../../../../ReduxStorage/SubscriptionPlans/constants';

export type CourseMarketPlaceSetupProps = {
  planType: SubscriptionPlanType;
  accountStatus: UserSubscriptionStripeConnectStatus;
  onSetupStripeAccountClick: (country: string) => Promise<void>;
};

const MarketPlaceDisabledAlert = (): ReactElement => (
  <AlertMessage
    kind="info"
    title="Revenue sharing is off"
    message={
      <span>
        Please reach out to{' '}
        <BannerLinkStyle href="mailto:support@openlearning.com">
          support@openlearning.com
        </BannerLinkStyle>{' '}
        to discuss enabling revenue sharing for your Marketplace courses.
      </span>
    }
  />
);

const NoStripeAccountAlert = (): ReactElement => (
  <AlertMessage
    kind="info"
    title="Set up your Stripe Connect account"
    message="To get paid for your courses that are listed on the marketplace for both learner enrolment and paid certification, please create a Stripe connect account."
  />
);

const COUNTRY_OPTIONS = makeCountryOptions();

const CourseMarketplaceSetup = ({
  planType,
  accountStatus,
  onSetupStripeAccountClick,
}: CourseMarketPlaceSetupProps): ReactElement => {
  const [country, setCountry] = useState('');
  const disabled =
    accountStatus === 'no_revenue_share' &&
    planType !== 'personal' &&
    planType !== 'portal';
  return (
    <div>
      <BannerContainer>
        {disabled ? (
          <MarketPlaceDisabledAlert />
        ) : accountStatus === 'no_stripe_account' ? (
          <NoStripeAccountAlert />
        ) : null}
      </BannerContainer>
      <InputContainer>
        <Select
          label="Country"
          description="Requires you to have a bank account in this country"
          value={country}
          options={COUNTRY_OPTIONS}
          onValueChange={setCountry}
          required
          disabled={disabled}
        />
        {accountStatus === 'no_stripe_account' &&
          country !== '' &&
          !StripeConnectCountries.includes(country) && (
            <SelectErrorContainer>
              Unfortunately Stripe Connect is currently available in your
              country and the payout process will be done manually. To review
              your course marketplace revenue sharing and payment terms, please{' '}
              <a href="mailto:support@openlearning.com">contact us</a>.
            </SelectErrorContainer>
          )}
      </InputContainer>
      <ActionsContainer>
        <ActionButton
          disabled={disabled || !StripeConnectCountries.includes(country)}
          onClick={() => onSetupStripeAccountClick(country)}
          colour="kelp"
          label="Set up your stripe account"
        />
      </ActionsContainer>
      <div>
        <span>
          Have a question about how Stripe Connect works?{' '}
          <NeedHelpLinkStyle href="mailto:support@openlearning.com">
            Contact us
          </NeedHelpLinkStyle>
        </span>
      </div>
    </div>
  );
};

export type CourseMarketplaceManagerProps = {
  accountStatus: UserSubscriptionStripeConnectStatus;
  goToStripeConnect: () => Promise<void>; // the link
  revenueShare: RevenueShare;
};

const CourseMarketplaceManager = ({
  accountStatus,
  goToStripeConnect,
  revenueShare,
}: CourseMarketplaceManagerProps): ReactElement => (
  <div>
    <Table>
      <tr>
        {!revenueShare.useOlConnect && <th>Stripe Connect Account</th>}
        <th>Processing fee</th>
        <th>Taxation</th>
        <th>Payout cycle</th>
        <th>Actions</th>
      </tr>
      <tr>
        {!revenueShare.useOlConnect && (
          <td>
            <Chip
              variant={getChipVariantFromStripeConnectStatus(accountStatus)}
              label={capitalizeFirstLetter(accountStatus)}
            />
          </td>
        )}
        <td>
          {revenueShare.processingFees
            ? 'Yes - deducted automatically '
            : 'No - absorbed by OpenLearning'}
        </td>
        <td>{TAX_LABEL[revenueShare.tax]}</td>
        <td>Weekly</td>
        <td>
          {revenueShare.useOlConnect ? (
            <RouteLink
              to={{
                type: ROUTE_CONTACT_PAGE,
              }}
            >
              Contact us
            </RouteLink>
          ) : (
            <StyledButton
              variant="legacy-kelp"
              action={{
                kind: 'on_click',
                value: () => goToStripeConnect(),
              }}
            >
              Go to Stripe Connect
            </StyledButton>
          )}
        </td>
      </tr>
    </Table>
    {/* Payment transaction history (phase 2?) */}
  </div>
);

export type CourseMarketplaceProps = {
  planType: SubscriptionPlanType;
  accountStatus: UserSubscriptionStripeConnectStatus;
  goToStripeConnect: () => Promise<void>;
  revenueShare: RevenueShare | null;
  onSetupStripeAccountClick: (country: string) => Promise<void>;
};

export const CourseMarketplace = ({
  planType,
  accountStatus,
  goToStripeConnect,
  revenueShare,
  onSetupStripeAccountClick,
}: CourseMarketplaceProps): ReactElement => (
  <div>
    <Header>
      <Heading>Course marketplace</Heading>
    </Header>
    {accountStatus === 'no_revenue_share' ||
    (accountStatus === 'no_stripe_account' && !revenueShare?.useOlConnect) ? (
      <CourseMarketplaceSetup
        {...{
          planType,
          accountStatus,
          onSetupStripeAccountClick,
        }}
      />
    ) : revenueShare ? (
      <CourseMarketplaceManager
        {...{
          revenueShare,
          accountStatus,
          goToStripeConnect,
        }}
      />
    ) : null}
  </div>
);
